import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CaretLeftIcon, CaretRightIcon, PlusIcon, SignInIcon, SignOutIcon, XCircleIcon, TrashIcon, EnvelopeIcon, ReceiptIcon, ArrowCounterClockwiseIcon, IdentificationCardIcon, MoonIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import GuestPicker from "@/components/GuestPicker";
import { GuestContactEdit } from "@/components/GuestContactEdit";
import { StayActions } from "@/components/StayActions";
import { ChannelChip, ALL_CHANNELS, CHANNEL_META } from "@/components/ChannelChip";
import GuestRegistrationCard from "@/pages/frontdesk/GuestRegistrationCard";
import DayEndDialog from "@/pages/frontdesk/DayEndDialog";

const DAY = 86400000;
const fmt = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (d, n) => fmt(new Date(new Date(d).getTime() + n * DAY));
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const STATUS_COLOR = {
  booked: "bg-primary text-primary-foreground",
  checked_in: "bg-secondary text-secondary-foreground",
  checked_out: "bg-muted text-muted-foreground",
};

function TodayActionsStrip({ reservations, onChanged, onOpenBill }) {
  const today = fmt(new Date());
  const arrivals = reservations.filter((r) => r.status === "booked" && r.check_in === today);
  const departures = reservations.filter((r) => r.status === "checked_in" && r.check_out === today);
  const inhouse = reservations.filter((r) => r.status === "checked_in" && r.check_out !== today);

  const doAction = async (fn, label) => {
    try { await fn(); toast.success(label); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const checkIn = (r) => doAction(() => api.patch(`/reservations/${r.id}`, { status: "checked_in" }), `${r.guest_name} checked in`);
  const checkoutAndBill = (r) => onOpenBill(r);
  const undoCheckin = (r) => doAction(() => api.post(`/reservations/${r.id}/cancel-checkin`), `Reversed check-in for ${r.guest_name}`);
  const undoCheckout = (r) => doAction(() => api.post(`/reservations/${r.id}/cancel-checkout`), `Reversed check-out for ${r.guest_name}`);

  if (arrivals.length + departures.length + inhouse.length === 0) return null;

  const Row = ({ title, count, colour, items, primaryLabel, primaryIcon: Icon, onPrimary, undoLabel, onUndo, testid }) => (
    <div className="flex-1 min-w-[260px]" data-testid={testid}>
      <div className="flex items-center justify-between px-1 mb-1">
        <p className="label-overline">{title}</p>
        <Badge variant="outline" className={colour}>{count}</Badge>
      </div>
      <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
        {items.slice(0, 5).map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-2 border border-border rounded-md px-2 py-1.5 text-xs">
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{r.guest_name}</p>
              <p className="text-[10px] text-muted-foreground">Room {r.room_number || r.room_id?.slice(0,4)} · {r.channel || "direct"}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" onClick={() => onPrimary(r)} className="h-7 px-2 text-[11px]" data-testid={`${testid}-primary-${r.id}`}>
                <Icon size={11} className="mr-0.5" /> {primaryLabel}
              </Button>
              {onUndo && (
                <Button size="icon" variant="ghost" onClick={() => onUndo(r)} className="h-7 w-7" title={undoLabel} data-testid={`${testid}-undo-${r.id}`}>
                  <ArrowCounterClockwiseIcon size={11} />
                </Button>
              )}
            </div>
          </div>
        ))}
        {items.length > 5 && <p className="text-[10px] text-muted-foreground text-center py-1">+{items.length - 5} more…</p>}
      </div>
    </div>
  );

  return (
    <Card className="p-4 mb-4 flex flex-wrap gap-4" data-testid="timeline-today-strip">
      {arrivals.length > 0 && (
        <Row
          title="Today's arrivals" count={arrivals.length}
          colour="bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"
          items={arrivals}
          primaryLabel="Check-in" primaryIcon={SignInIcon}
          onPrimary={checkIn}
          testid="today-arrivals"
        />
      )}
      {departures.length > 0 && (
        <Row
          title="Departing today" count={departures.length}
          colour="bg-primary/15 text-primary"
          items={departures}
          primaryLabel="C-out & Bill" primaryIcon={ReceiptIcon}
          onPrimary={checkoutAndBill}
          undoLabel="Undo previous check-in (mistake)"
          onUndo={undoCheckin}
          testid="today-departures"
        />
      )}
      {inhouse.length > 0 && (
        <Row
          title="In-house · check-out early?" count={inhouse.length}
          colour="bg-muted text-muted-foreground"
          items={inhouse}
          primaryLabel="C-out & Bill" primaryIcon={ReceiptIcon}
          onPrimary={checkoutAndBill}
          undoLabel="Undo check-in (mistake)"
          onUndo={undoCheckin}
          testid="today-inhouse"
        />
      )}
    </Card>
  );
}


function CheckoutBillDialog({ res, onClose, onDone }) {
  // Add extra charges & discount before generating the invoice — the front-desk workflow.
  const [extras, setExtras] = useState([]);   // [{label, amount}]
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(12);
  const [taxInclusive, setTaxInclusive] = useState(true);
  const [corporate, setCorporate] = useState({ enabled: false, name: "", gstin: "", address: "", pan: "" });
  const [paymentMode, setPaymentMode] = useState("cash");
  const [paymentRef, setPaymentRef] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/platform").then((r) => {
      const p = r.data || {};
      setTaxRate(Number(p.default_tax_rate) || 12);
      setTaxInclusive(!!p.default_tax_inclusive);
    }).catch(() => {});
  }, []);

  const addExtra = (label = "", amount = 0) => setExtras([...extras, { label, amount }]);
  const patchExtra = (i, patch) => setExtras(extras.map((e, idx) => idx === i ? { ...e, ...patch } : e));
  const removeExtra = (i) => setExtras(extras.filter((_, idx) => idx !== i));

  const roomCharges = Number(res?.total_amount || 0);
  const extrasTotal = extras.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const gross = roomCharges + extrasTotal - Number(discount || 0);
  let tax = 0, total = gross;
  if (taxInclusive) {
    const taxable = taxRate ? gross / (1 + taxRate / 100) : gross;
    tax = gross - taxable;
    total = gross;
  } else {
    tax = gross * (Number(taxRate) || 0) / 100;
    total = gross + tax;
  }

  const finalise = async (markPaid) => {
    setBusy(true);
    try {
      // 1) checkout the reservation (updates room + creates HK task)
      await api.patch(`/reservations/${res.id}`, { status: "checked_out" });
      // 2) generate the invoice with extras + tax settings baked in
      const inv = await api.post(`/invoices/generate/${res.id}`, null, {
        params: {
          extra_charges: extrasTotal,
          discount: Number(discount) || 0,
          tax_rate: Number(taxRate) || 0,
          tax_inclusive: taxInclusive,
        },
      });
      const invoiceId = inv.data.id;
      // 3) apply corporate billing metadata + attach zero-value narration lines for the extras
      // (extras are already counted in extra_charges — narration lines must NOT be chargeable
      //  else totals double-count.)
      const invPatch = {};
      if (corporate.enabled) {
        invPatch.bill_to_corporate = true;
        invPatch.corporate_name = corporate.name;
        invPatch.corporate_gstin = corporate.gstin;
        invPatch.corporate_address = corporate.address;
        invPatch.corporate_pan = corporate.pan;
      }
      if (extras.length) {
        const currentLines = inv.data.line_items || [];
        const extraLines = extras.filter((e) => e.label).map((e) => ({
          label: `${e.label} (₹${Number(e.amount || 0)})`,
          description: `${e.label} (₹${Number(e.amount || 0)})`,
          qty: 0, rate: 0, amount: 0,   // narration only — total is already in extra_charges
          kind: "extra_narration",
        }));
        invPatch.line_items = [...currentLines, ...extraLines];
      }
      if (Object.keys(invPatch).length) {
        await api.patch(`/invoices/${invoiceId}`, invPatch);
      }
      // 4) mark paid if requested — always use the persisted invoice total, not the front-end estimate
      if (markPaid) {
        try {
          const fresh = await api.get(`/invoices/${invoiceId}`);
          const trueTotal = Number(fresh.data?.total || 0);
          await api.post(`/invoices/${invoiceId}/mark-paid`, {
            amount: trueTotal,
            payment_mode: paymentMode,
            payment_reference: paymentRef,
          });
        } catch (err) { /* non-blocking */ }
      }
      toast.success(`Bill ${inv.data.number} generated${markPaid ? " and marked paid" : ""}`);
      onDone && onDone();
      onClose();
      setTimeout(() => window.location.assign(`/invoices`), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout & bill failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!res} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="checkout-bill-dialog">
        <DialogHeader>
          <p className="label-overline">Check-out &amp; Bill</p>
          <DialogTitle className="font-display text-2xl">{res?.guest_name}</DialogTitle>
          <p className="text-xs text-muted-foreground">Room {res?.room_number} · {res?.check_in} → {res?.check_out} · {res?.plan_code || "EP"} plan</p>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="p-3 bg-muted/30 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Room + plan + extras (booking total)</span><span className="font-mono-plex">{inr(roomCharges)}</span></div>
          </Card>

          {/* Extra charges builder */}
          <div className="rounded-md border border-border p-3" data-testid="checkout-bill-extras">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Extra charges (mini-bar, laundry, room service, phone, breakage…)</p>
              <Button type="button" size="sm" variant="outline" onClick={() => addExtra("", 0)} data-testid="checkout-bill-add-extra"><PlusIcon size={10} className="mr-1" /> Add</Button>
            </div>
            {extras.length === 0 && <p className="text-xs text-muted-foreground">No extras added. Click &quot;Add&quot; for mini-bar, laundry, etc.</p>}
            {extras.map((e, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 mt-2 items-center" data-testid={`checkout-bill-extra-${idx}`}>
                <div className="col-span-7"><Input placeholder="e.g. Mini-bar · 2 beers" value={e.label} onChange={(ev) => patchExtra(idx, { label: ev.target.value })} data-testid={`checkout-bill-extra-label-${idx}`} /></div>
                <div className="col-span-4"><Input type="number" min="0" placeholder="Amount ₹" value={e.amount} onChange={(ev) => patchExtra(idx, { amount: ev.target.value })} data-testid={`checkout-bill-extra-amount-${idx}`} /></div>
                <div className="col-span-1"><Button type="button" size="icon" variant="ghost" onClick={() => removeExtra(idx)} className="text-destructive"><XCircleIcon size={12} /></Button></div>
              </div>
            ))}
            {[["Mini-bar",150],["Laundry",250],["Room service",300],["Late check-out",500]].map(([lbl,amt]) => (
              <Button key={lbl} type="button" size="sm" variant="ghost" onClick={() => addExtra(lbl, amt)} className="mt-2 mr-2 text-[10px] h-6 px-2" data-testid={`checkout-bill-quickadd-${lbl}`}>
                + {lbl} · ₹{amt}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Discount ₹</Label><Input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="checkout-bill-discount" /></div>
            <div><Label className="text-xs">GST rate %</Label><Input type="number" min="0" step="0.5" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="checkout-bill-tax-rate" /></div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={taxInclusive} onChange={(e) => setTaxInclusive(e.target.checked)} className="accent-primary" data-testid="checkout-bill-tax-incl" />
                Prices inclusive of GST
              </label>
            </div>
          </div>

          {/* Corporate billing */}
          <div className="rounded-md border border-border p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={corporate.enabled} onChange={(e) => setCorporate({ ...corporate, enabled: e.target.checked })} className="accent-primary" data-testid="checkout-bill-corp-toggle" />
              <span className="font-medium">Bill to a company (Corporate GST)</span>
            </label>
            {corporate.enabled && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="col-span-2"><Label className="text-xs">Company name</Label><Input value={corporate.name} onChange={(e) => setCorporate({ ...corporate, name: e.target.value })} data-testid="checkout-bill-corp-name" /></div>
                <div><Label className="text-xs">GSTIN</Label><Input value={corporate.gstin} onChange={(e) => setCorporate({ ...corporate, gstin: e.target.value })} data-testid="checkout-bill-corp-gstin" /></div>
                <div><Label className="text-xs">PAN (optional)</Label><Input value={corporate.pan} onChange={(e) => setCorporate({ ...corporate, pan: e.target.value })} /></div>
                <div className="col-span-2"><Label className="text-xs">Billing address</Label><Input value={corporate.address} onChange={(e) => setCorporate({ ...corporate, address: e.target.value })} /></div>
              </div>
            )}
          </div>

          {/* Payment (optional inline) */}
          <div className="rounded-md border border-border p-3">
            <p className="text-sm font-medium mb-2">Collect payment now (optional)</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1">
                <Label className="text-xs">Mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger className="h-9" data-testid="checkout-bill-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="travels">Travels</SelectItem>
                    <SelectItem value="corporate">Corporate credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Reference (UPI txn, card 4 digits…)</Label><Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} data-testid="checkout-bill-pay-ref" /></div>
            </div>
          </div>

          {/* Live totals */}
          <Card className="p-3 bg-primary/5 border-primary/40 text-sm">
            <div className="flex justify-between"><span>Room + plan + extras</span><span className="font-mono-plex">{inr(roomCharges)}</span></div>
            <div className="flex justify-between"><span>Extras added here</span><span className="font-mono-plex">+{inr(extrasTotal)}</span></div>
            <div className="flex justify-between"><span>Discount</span><span className="font-mono-plex">-{inr(discount)}</span></div>
            <div className="flex justify-between"><span>GST {taxInclusive ? "(inclusive)" : "(added)"}</span><span className="font-mono-plex">{inr(tax)}</span></div>
            <div className="flex justify-between font-display text-lg mt-2 pt-2 border-t border-primary/40"><span>Total</span><span className="text-primary" data-testid="checkout-bill-total">{inr(total)}</span></div>
          </Card>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="outline" onClick={() => finalise(false)} disabled={busy} data-testid="checkout-bill-save-draft">
            {busy ? "Saving…" : "Save bill (unpaid)"}
          </Button>
          <Button onClick={() => finalise(true)} disabled={busy} data-testid="checkout-bill-finalise">
            {busy ? "Processing…" : `Finalise & mark paid · ${inr(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReservationDetail({ resId, onClose, onChanged, rooms, platform, onOpenBill }) {
  const [res, setRes] = useState(null);
  const [form, setForm] = useState({ check_in: "", check_out: "", room_id: "", adults: 1, children: 0, plan_code: "EP", extra_bed_count: 0, cnb_count: 0, early_checkin_no_charge: false, late_checkout_no_charge: false });
  const [busy, setBusy] = useState(false);
  // Default to preserving the guest's existing per-night rate on room moves so
  // an operational move / courtesy upgrade doesn't silently reprice the stay.
  const [keepRate, setKeepRate] = useState(true);
  useEffect(() => {
    (async () => {
      const list = await api.get("/reservations");
      const r = list.data.find((x) => x.id === resId);
      if (r) {
        setRes(r);
        setForm({
          check_in: r.check_in, check_out: r.check_out, room_id: r.room_id,
          adults: r.adults || 1, children: r.children || 0,
          plan_code: r.plan_code || "EP",
          extra_bed_count: r.extra_bed_count || 0,
          cnb_count: r.cnb_count || 0,
          early_checkin_no_charge: !!r.early_checkin_no_charge,
          late_checkout_no_charge: !!r.late_checkout_no_charge,
        });
      }
    })();
  }, [resId]);
  if (!res) return <DialogContent className="max-h-[90vh] overflow-y-auto"><p className="p-6">Loading…</p></DialogContent>;

  const save = async () => {
    if (form.check_out <= form.check_in) return toast.error("Check-out must be at least 1 day after check-in");
    setBusy(true);
    try {
      // If the guest is being moved to a different room, honor the rate choice
      const payload = { ...form };
      if (res && form.room_id && form.room_id !== res.room_id) {
        payload.keep_existing_rate = keepRate;
      }
      await api.patch(`/reservations/${res.id}`, payload);
      toast.success(payload.keep_existing_rate === true ? "Reservation updated · rate preserved" : "Reservation updated");
      onChanged();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try {
      await api.patch(`/reservations/${res.id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      onChanged();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openBillDialog = () => {
    onClose();
    onOpenBill && onOpenBill({
      ...res,
      guest_name: res.guest?.name,
      room_number: res.room?.number,
    });
  };
  const undoCheckin = async () => {
    if (!window.confirm("Undo check-in and mark the room available again?")) return;
    try { await api.post(`/reservations/${res.id}/cancel-checkin`); toast.success("Check-in reversed"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const undoCheckout = async () => {
    if (!window.confirm("Undo check-out? The draft invoice (if any) will be deleted.")) return;
    try { await api.post(`/reservations/${res.id}/cancel-checkout`); toast.success("Check-out reversed"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this reservation?")) return;
    await api.delete(`/reservations/${res.id}`);
    toast.success("Deleted");
    onChanged();
    onClose();
  };

  return (
    <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center justify-between">
          <span>{res.guest?.name}</span>
          <Badge variant="outline">{res.status?.replace("_", " ")}</Badge>
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="rounded-md bg-muted/60 p-3 text-xs space-y-1">
          <div><span className="text-muted-foreground">Room:</span> {res.room?.number} · {res.room?.room_type}</div>
          <div><span className="text-muted-foreground">Total:</span> <span className="font-mono-plex">{inr(res.total_amount)}</span> ({res.payment_status})</div>
          <div className="flex items-center justify-between gap-2">
            <span data-testid="res-detail-contact"><span className="text-muted-foreground">Contact:</span> {res.guest?.phone}{res.guest?.email && ` · ${res.guest.email}`}</span>
            <GuestContactEdit guest={res.guest} onSaved={(g) => { setRes({ ...res, guest: g }); onChanged(); }} testIdPrefix="res-detail-contact" />
          </div>
          {res.plan_code && <div><span className="text-muted-foreground">Plan:</span> {res.plan_code} · Extras — bed×{res.extra_bed_count || 0} cnb×{res.cnb_count || 0}</div>}
        </div>
        <StayActions res={res} onChanged={async () => {
          const r = await api.get("/reservations");
          const fresh = r.data.find((x) => x.id === res.id);
          if (fresh) setRes(fresh);
          onChanged();
        }} testIdPrefix="res-detail" />

        <div className="grid grid-cols-2 gap-3">
          <div><Label>Check-in</Label><Input type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="res-detail-checkin" /></div>
          <div><Label>Check-out</Label><Input type="date" value={form.check_out} min={form.check_in ? addDays(form.check_in, 1) : undefined} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="res-detail-checkout" /></div>
        </div>
        <div>
          <Label>Move to room</Label>
          <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
            <SelectTrigger data-testid="res-detail-room"><SelectValue /></SelectTrigger>
            <SelectContent>
              {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.room_type} · {inr(r.rate_per_night)}</SelectItem>)}
            </SelectContent>
          </Select>
          {res && form.room_id && form.room_id !== res.room_id && (
            <div className="mt-2 rounded-md border border-secondary/40 bg-secondary/10 p-3 space-y-2" data-testid="res-detail-move-rate">
              <p className="text-xs font-medium text-secondary uppercase tracking-widest">Room change · pick a billing rule</p>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="move-rate-choice"
                  checked={keepRate}
                  onChange={() => setKeepRate(true)}
                  data-testid="res-detail-keep-rate"
                  className="mt-1"
                />
                <span>
                  <strong>Keep current rate</strong> — the guest&apos;s existing ₹{inr(res.rate_per_night)}/night stays on the bill. Total scales with nights only.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="move-rate-choice"
                  checked={!keepRate}
                  onChange={() => setKeepRate(false)}
                  data-testid="res-detail-new-rate"
                  className="mt-1"
                />
                <span>
                  <strong>Apply new room&apos;s rate</strong> — reprice the stay at the target room&apos;s rack rate (with any calendar overrides).
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Adults</Label><Input type="number" min="1" value={form.adults} onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} /></div>
          <div><Label>Children</Label><Input type="number" min="0" value={form.children} onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} /></div>
        </div>

        {/* Meal plan + extras — always visible in Front Desk detail */}
        <div className="rounded-md border border-border p-3 space-y-3" data-testid="res-detail-plan-extras">
          <div>
            <Label className="text-sm">Meal plan</Label>
            <Select value={form.plan_code} onValueChange={(v) => setForm({ ...form, plan_code: v })}>
              <SelectTrigger className="mt-1" data-testid="res-detail-plan-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(platform?.meal_plans || [{code:"EP",label:"European Plan"}]).map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.code} — {p.label}{p.surcharge_per_person > 0 ? ` (+₹${p.surcharge_per_person}/pax/nt)` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Extra beds (₹{platform?.extra_bed_rate || 500}/nt)</Label><Input type="number" min="0" value={form.extra_bed_count} onChange={(e) => setForm({ ...form, extra_bed_count: Number(e.target.value) })} data-testid="res-detail-extra-bed" /></div>
            <div><Label className="text-xs">CNB (₹{platform?.cnb_rate || 300}/nt)</Label><Input type="number" min="0" value={form.cnb_count} onChange={(e) => setForm({ ...form, cnb_count: Number(e.target.value) })} data-testid="res-detail-cnb" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.early_checkin_no_charge} onChange={(e) => setForm({ ...form, early_checkin_no_charge: e.target.checked })} className="accent-primary" data-testid="res-detail-early-free" />Complimentary early check-in</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.late_checkout_no_charge} onChange={(e) => setForm({ ...form, late_checkout_no_charge: e.target.checked })} className="accent-primary" data-testid="res-detail-late-free" />Complimentary late check-out</label>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
          {res.status === "booked" && <Button size="sm" variant="outline" onClick={() => setStatus("checked_in")} data-testid="res-detail-checkin-btn"><SignInIcon size={14} className="mr-1" />Check-in</Button>}
          {res.status === "checked_in" && (
            <>
              <Button size="sm" onClick={openBillDialog} data-testid="res-detail-checkout-bill-btn"><SignOutIcon size={14} className="mr-1" />Check-out &amp; Bill</Button>
              <Button size="sm" variant="ghost" onClick={undoCheckin} data-testid="res-detail-undo-checkin-btn" title="Undo the check-in (mistake)">↺ Undo check-in</Button>
            </>
          )}
          {res.status === "checked_out" && (
            <Button size="sm" variant="ghost" onClick={undoCheckout} data-testid="res-detail-undo-checkout-btn" title="Undo the check-out (mistake)">↺ Undo check-out</Button>
          )}
          <Button size="sm" variant="outline" onClick={async () => {
            try { const r = await api.post(`/reservations/${res.id}/send-confirmation`, {}); toast.success(`Emailed ${r.data.sent_to}`); }
            catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
          }} data-testid="res-detail-send-confirmation"><EnvelopeIcon size={14} className="mr-1" />Send confirmation</Button>
          {["booked", "checked_in"].includes(res.status) && <Button size="sm" variant="ghost" onClick={() => setStatus("cancelled")} data-testid="res-detail-cancel-btn"><XCircleIcon size={14} className="mr-1" />Cancel</Button>}
          <Button size="sm" variant="ghost" className="text-destructive ml-auto" onClick={del}><TrashIcon size={14} /></Button>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={save} disabled={busy} data-testid="res-detail-save-btn">{busy ? "Saving…" : "Save changes"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function Timeline() {
  const { activeId, active } = useProperty();
  const [start, setStart] = useState(fmt(new Date()));
  const [view, setView] = useState("week"); // week | month | quarter | year
  const viewDays = { week: 7, month: 30, quarter: 90, year: 365 }[view];
  const [data, setData] = useState({ dates: [], rooms: [], reservations: [] });
  const [guests, setGuests] = useState([]);
  const [openBooking, setOpenBooking] = useState(null);
  const [openRes, setOpenRes] = useState(null);
  const [billTarget, setBillTarget] = useState(null);
  const [form, setForm] = useState({ guest_id: "", check_out: "", adults: 1, children: 0, channel: "direct", plan_code: "EP", extra_bed_count: 0, cnb_count: 0, early_checkin_no_charge: false, late_checkout_no_charge: false });
  const [platform, setPlatform] = useState({ meal_plans: [], extra_bed_rate: 500, cnb_rate: 300 });
  const [grcOpen, setGrcOpen] = useState(false);
  const [dayEndOpen, setDayEndOpen] = useState(false);
  useEffect(() => { api.get("/settings/platform").then((r) => setPlatform(r.data || {})).catch(() => {}); }, []);

  const end = addDays(start, viewDays);
  const columnWidth = { week: 90, month: 38, quarter: 20, year: 8 }[view];
  const cellHeight = { week: 64, month: 56, quarter: 44, year: 34 }[view];

  const load = () => {
    if (!activeId) return;
    api.get("/timeline", { params: { property_id: activeId, start, end } }).then((r) => setData(r.data));
    api.get("/guests").then((r) => setGuests(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, start, view]);

  const barsByRoom = useMemo(() => {
    const map = {};
    const dateIdx = Object.fromEntries(data.dates.map((d, i) => [d, i]));
    (data.rooms || []).forEach((r) => (map[r.id] = []));
    (data.reservations || []).forEach((res) => {
      const ci = new Date(Math.max(new Date(res.check_in), new Date(start)));
      const co = new Date(Math.min(new Date(res.check_out), new Date(end)));
      const s = dateIdx[fmt(ci)];
      const e = dateIdx[fmt(co)] ?? data.dates.length;
      if (s == null) return;
      const span = Math.max(1, e - s);
      (map[res.room_id] || []).push({ res, startIdx: s, span });
    });
    return map;
  }, [data, start, end]);

  const openBook = (roomId, date) => {
    const today = fmt(new Date());
    if (date < today) {
      toast.error("Cannot book past dates — pick today or a future date.");
      return;
    }
    setForm({ guest_id: "", check_out: addDays(date, 1), adults: 1, children: 0, channel: "direct", rate_override: "", deposit_amount: "", deposit_mode: "cash", deposit_reference: "", plan_code: "EP", extra_bed_count: 0, cnb_count: 0, early_checkin_no_charge: false, late_checkout_no_charge: false });
    setOpenBooking({ room_id: roomId, date });
  };

  const submitBook = async (e) => {
    e.preventDefault();
    if (!form.guest_id) return toast.error("Please pick a guest — reservations must have guest details.");
    if (!form.check_out || form.check_out <= openBooking.date) return toast.error("Check-out must be at least 1 day after check-in.");
    try {
      const payload = {
        property_id: activeId,
        room_id: openBooking.room_id,
        guest_id: form.guest_id,
        check_in: openBooking.date,
        check_out: form.check_out,
        adults: Number(form.adults),
        children: Number(form.children) || 0,
        channel: form.channel,
        plan_code: form.plan_code,
        extra_bed_count: Number(form.extra_bed_count) || 0,
        cnb_count: Number(form.cnb_count) || 0,
        early_checkin_no_charge: !!form.early_checkin_no_charge,
        late_checkout_no_charge: !!form.late_checkout_no_charge,
      };
      const rov = Number(form.rate_override);
      if (form.rate_override !== "" && !isNaN(rov) && rov > 0) {
        payload.rate_override = rov;
      }
      const dep = Number(form.deposit_amount);
      if (form.deposit_amount !== "" && !isNaN(dep) && dep > 0) {
        payload.deposit_amount = dep;
        payload.deposit_mode = form.deposit_mode || "cash";
        if (form.deposit_reference) payload.deposit_reference = form.deposit_reference;
      }
      await api.post("/reservations", payload);
      toast.success("Booked");
      setOpenBooking(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    }
  };

  const stepDays = { week: 7, month: 30, quarter: 90, year: 365 }[view];

  return (
    <div>
      <PageHeader
        overline="Front desk"
        title="Timeline"
        description={active ? `${active.name} · click a bar to edit, or a free cell to book` : "Rooms × dates grid"}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 border border-border rounded-md p-0.5" data-testid="timeline-view-switcher">
              {["week", "month", "quarter", "year"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1 text-xs uppercase tracking-wider rounded transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`timeline-view-${v}`}
                >
                  {v}
                </button>
              ))}
            </div>
            <Button variant="outline" size="icon" onClick={() => setStart(addDays(start, -stepDays))} data-testid="timeline-prev"><CaretLeftIcon size={14} /></Button>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="timeline-date" />
            <Button variant="outline" size="icon" onClick={() => setStart(addDays(start, stepDays))} data-testid="timeline-next"><CaretRightIcon size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => setStart(fmt(new Date()))}>Today</Button>
            <div className="mx-1 h-6 w-px bg-border" />
            <Button variant="outline" size="sm" onClick={() => setGrcOpen(true)} data-testid="timeline-grc-btn"><IdentificationCardIcon size={14} className="mr-1" />GRC</Button>
            <Button variant="outline" size="sm" onClick={() => setDayEndOpen(true)} data-testid="timeline-dayend-btn"><MoonIcon size={14} className="mr-1" />Day End</Button>
          </div>
        }
      />

      {/* Today's operations — quick check-in / check-out & bill buttons */}
      <TodayActionsStrip reservations={data.reservations || []} onChanged={load} onOpenBill={setBillTarget} />

      <Card className="overflow-x-auto">
        <div className="min-w-max">
          <div className="flex sticky top-0 z-10 bg-card border-b border-border">
            <div className="w-40 shrink-0 p-3 label-overline border-r border-border">Room</div>
            {data.dates.map((d) => {
              const dt = new Date(d);
              const wd = dt.toLocaleDateString("en-IN", { weekday: "short" });
              const isWknd = [0, 6].includes(dt.getDay());
              const isFirstOfMonth = dt.getDate() === 1;
              // In compact views, show month label on 1st only
              const showText = view === "week" || view === "month" || (view === "quarter" && (dt.getDate() === 1 || dt.getDate() % 5 === 0)) || (view === "year" && isFirstOfMonth);
              return (
                <div key={d} style={{ width: columnWidth }} className={`shrink-0 text-center py-2 border-r border-border/60 text-[10px] ${isWknd ? "bg-muted/40" : ""} ${isFirstOfMonth ? "border-l-2 border-l-primary/40" : ""}`}>
                  {view === "year" ? (
                    isFirstOfMonth && <div className="uppercase text-[10px] font-medium">{dt.toLocaleDateString("en-IN", { month: "short" })}</div>
                  ) : (
                    <>
                      {showText && <div className="uppercase text-[9px] tracking-widest text-muted-foreground">{wd}</div>}
                      <div className="font-mono-plex">{d.slice(8, 10)}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {(data.rooms || []).map((room) => (
            <div key={room.id} className="flex border-b border-border/40 relative hover:bg-muted/30">
              <div className="w-40 shrink-0 p-3 border-r border-border sticky left-0 bg-card">
                <p className="font-mono-plex text-sm">{room.number}</p>
                <p className="text-[11px] text-muted-foreground">{room.room_type}</p>
              </div>
              {data.dates.map((d, idx) => (
                <button
                  key={d + idx}
                  onClick={() => openBook(room.id, d)}
                  className="shrink-0 border-r border-border/40 hover:bg-primary/5"
                  style={{ width: columnWidth, height: cellHeight }}
                  data-testid={`timeline-cell-${room.number}-${d}`}
                />
              ))}
              {(barsByRoom[room.id] || []).map(({ res, startIdx, span }) => {
                const barWidth = span * columnWidth - 4;
                const compact = barWidth < 90;
                return (
                  <button
                    key={res.id}
                    onClick={() => setOpenRes(res.id)}
                    className={`absolute top-1 rounded ${STATUS_COLOR[res.status] || "bg-primary text-primary-foreground"} px-1.5 flex items-center gap-1.5 shadow-sm hover:brightness-110 overflow-hidden`}
                    style={{
                      left: 160 + startIdx * columnWidth + 2,
                      width: barWidth,
                      height: cellHeight - 8,
                    }}
                    title={`${res.guest_name} · ${res.check_in} → ${res.check_out} · ${CHANNEL_META[res.channel || "direct"]?.label || "Direct"}`}
                    data-testid={`timeline-bar-${res.id}`}
                  >
                    <ChannelChip channel={res.channel || "direct"} size={compact ? 12 : 16} />
                    {!compact && <span className="truncate text-xs font-medium">{res.guest_name}</span>}
                  </button>
                );
              })}
            </div>
          ))}
          {data.rooms.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No rooms in this property yet.</div>}
        </div>
      </Card>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
        <div className="flex gap-4">
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-primary" /> Booked</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-secondary" /> Checked-in</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-muted border border-border" /> Checked-out</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="uppercase tracking-widest text-[10px]">Channels</span>
          {["direct", "booking_com", "mmt", "yatra", "agoda", "airbnb", "goibibo", "public_widget"].map((c) => (
            <span key={c} className="flex items-center gap-1.5"><ChannelChip channel={c} size={14} />{CHANNEL_META[c].label}</span>
          ))}
        </div>
      </div>

      {/* Quick book */}
      <Dialog open={!!openBooking} onOpenChange={(v) => !v && setOpenBooking(null)}>
        <DialogContent className="max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
          <DialogHeader className="shrink-0 p-6 pb-3 border-b border-border"><DialogTitle>Quick book · {openBooking?.date}</DialogTitle></DialogHeader>
          <form onSubmit={submitBook} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="timeline-book-scroll">
            <div>
              <Label>Guest</Label>
              <GuestPicker
                guests={guests}
                value={form.guest_id}
                onChange={(id) => setForm({ ...form, guest_id: id })}
                onGuestCreated={() => { api.get("/guests").then((r) => setGuests(r.data)); }}
                testIdPrefix="timeline-book-guest"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Check-in</Label><Input type="date" value={openBooking?.date || ""} disabled /></div>
              <div><Label>Check-out</Label><Input type="date" value={form.check_out} min={openBooking?.date ? addDays(openBooking.date, 1) : undefined} onChange={(e) => setForm({ ...form, check_out: e.target.value })} data-testid="timeline-book-checkout" required /></div>
            </div>

            {/* Meal plan + extras */}
            <div className="rounded-md border border-border p-3 space-y-3" data-testid="timeline-book-plan-extras">
              <div>
                <Label className="text-sm">Meal plan</Label>
                <Select value={form.plan_code} onValueChange={(v) => setForm({ ...form, plan_code: v })}>
                  <SelectTrigger className="mt-1" data-testid="timeline-book-plan-code"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(platform.meal_plans || [{code:"EP",label:"European Plan (Room only)",surcharge_per_person:0}]).map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.code} — {p.label}{p.surcharge_per_person > 0 ? ` (+₹${p.surcharge_per_person}/pax/nt)` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Extra beds (₹{platform.extra_bed_rate || 500}/nt)</Label><Input type="number" min="0" value={form.extra_bed_count} onChange={(e) => setForm({ ...form, extra_bed_count: Number(e.target.value) })} data-testid="timeline-book-extra-bed" /></div>
                <div><Label className="text-xs">CNB — Child No Bed (₹{platform.cnb_rate || 300}/nt)</Label><Input type="number" min="0" value={form.cnb_count} onChange={(e) => setForm({ ...form, cnb_count: Number(e.target.value) })} data-testid="timeline-book-cnb" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.early_checkin_no_charge} onChange={(e) => setForm({ ...form, early_checkin_no_charge: e.target.checked })} className="accent-primary" data-testid="timeline-book-early-free" />Complimentary early check-in</label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.late_checkout_no_charge} onChange={(e) => setForm({ ...form, late_checkout_no_charge: e.target.checked })} className="accent-primary" data-testid="timeline-book-late-free" />Complimentary late check-out</label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div><Label>Adults</Label><Input type="number" min="1" value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} data-testid="timeline-book-adults" /></div>
              <div><Label>Children</Label><Input type="number" min="0" value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} data-testid="timeline-book-children" /></div>
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="timeline-book-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALL_CHANNELS.map((c) => (
                      <SelectItem key={c} value={c}>
                        <span className="flex items-center gap-2">
                          <ChannelChip channel={c} size={14} />
                          {CHANNEL_META[c].label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(() => {
              const room = (data.rooms || []).find((rm) => rm.id === openBooking?.room_id);
              const base = room?.rate_per_night || 0;
              return (
                <div className="rounded-md border border-border p-3">
                  <div className="flex items-baseline justify-between">
                    <Label className="text-sm">Rate per night (₹) — one-time override</Label>
                    {base > 0 && <span className="text-[10px] text-muted-foreground font-mono-plex">Base ₹{Number(base).toLocaleString("en-IN")}</span>}
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.rate_override || ""}
                    onChange={(e) => setForm({ ...form, rate_override: e.target.value })}
                    placeholder={`Leave blank to use base rate (${base})`}
                    data-testid="timeline-book-rate-override"
                    className="mt-1"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Applies only to this booking · does not touch OTA rates.</p>
                </div>
              );
            })()}
            <div className="rounded-md border border-border p-3">
              <Label className="text-sm">Advance / Deposit at booking (₹)</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.deposit_amount || ""}
                  onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })}
                  data-testid="timeline-book-deposit-amount"
                />
                <Select value={form.deposit_mode || "cash"} onValueChange={(v) => setForm({ ...form, deposit_mode: v })}>
                  <SelectTrigger data-testid="timeline-book-deposit-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Ref / UTR"
                  value={form.deposit_reference || ""}
                  onChange={(e) => setForm({ ...form, deposit_reference: e.target.value })}
                  data-testid="timeline-book-deposit-ref"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Adjusted from the check-out invoice automatically.</p>
            </div>
            </div>
            <DialogFooter className="shrink-0 border-t border-border px-6 py-3 bg-background">
              <Button type="button" variant="outline" onClick={() => setOpenBooking(null)}>Cancel</Button>
              <Button type="submit" data-testid="timeline-book-submit"><PlusIcon size={14} className="mr-1" />Book</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reservation detail on bar click */}
      <Dialog open={!!openRes} onOpenChange={(v) => !v && setOpenRes(null)}>
        {openRes && <ReservationDetail resId={openRes} onClose={() => setOpenRes(null)} onChanged={load} rooms={data.rooms || []} platform={platform} onOpenBill={setBillTarget} />}
      </Dialog>

      {billTarget && (
        <CheckoutBillDialog res={billTarget} onClose={() => setBillTarget(null)} onDone={load} />
      )}

      <GuestRegistrationCard open={grcOpen} onClose={() => setGrcOpen(false)} />
      <DayEndDialog open={dayEndOpen} onClose={() => setDayEndOpen(false)} />
    </div>
  );
}
